// Profile + Change Password + Onboarding — Sprint 28b.
import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/homepage/Header";
import { Footer } from "@/components/homepage/Footer";
import { useAuth } from "@/context/AuthContext";
import * as authApi from "@/services/authService";

const COUNTRIES = ["السعودية","الإمارات","قطر","الكويت","البحرين","سلطنة عُمان","مصر","المغرب","الأردن","لبنان","تونس","الجزائر","العراق","فلسطين"];
const INDUSTRIES = ["الطاقة","الذكاء الاصطناعي","التصنيع","الرعاية الصحية","العقارات","اللوجستيات","التقنية المالية","التجزئة","السياحة","التعليم","الزراعة","الاتصالات"];

const Shell = ({ title, subtitle, testId, children }) => (
  <div className="min-h-screen bg-surface-50 text-ink-900">
    <Header />
    <main data-testid={testId} className="mx-auto max-w-2xl px-6 py-14 lg:px-8">
      <h1 className="text-2xl font-bold tracking-tight text-ink-900 sm:text-3xl">{title}</h1>
      {subtitle && <p className="mt-2 text-sm text-ink-500">{subtitle}</p>}
      <div className="mt-8 space-y-8">{children}</div>
    </main>
    <Footer />
  </div>
);

const Card = ({ title, children, testId }) => (
  <section data-testid={testId} className="rounded-xl border border-surface-200 bg-white p-6">
    {title && <h2 className="mb-4 text-base font-semibold text-ink-900">{title}</h2>}
    {children}
  </section>
);

const Input = (p) => (
  <input {...p} className="block w-full rounded-md border border-surface-200 bg-white px-3 py-2.5 text-sm text-ink-900 focus:border-intel-500 focus:outline-none focus:ring-2 focus:ring-intel-100 disabled:bg-surface-50 disabled:text-ink-500" />
);

/* ─── Profile ─── */
export function ProfilePage() {
  const { user, isLoading, refreshUser, logout } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", country: "", industries: [] });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) setForm({
      name: user.full_name || "",
      country: user.preferred_country || "",
      industries: user.preferred_industries || [],
    });
  }, [user]);

  if (isLoading) return <Shell title="الملف الشخصي" testId="profile-loading">جاري التحميل…</Shell>;
  if (!user) return <Navigate to="/account/sign-in" replace state={{ from: { pathname: "/profile" } }} />;

  const toggle = (v) => setForm(f => ({ ...f, industries: f.industries.includes(v) ? f.industries.filter(x => x !== v) : [...f.industries, v] }));

  const onSave = async () => {
    setBusy(true);
    try {
      await authApi.updateProfile({ fullName: form.name.trim(), preferredCountry: form.country || null, preferredIndustries: form.industries });
      await refreshUser?.();
      toast.success("تمّ تحديث الملف الشخصي");
    } catch { toast.error("تعذّر الحفظ. حاول لاحقاً."); }
    finally { setBusy(false); }
  };

  const onLogout = async () => { try { await logout(); nav("/"); } catch { /* no-op */ } };
  const onResendVerify = async () => {
    try { await authApi.resendVerification(); toast.success("أرسلنا رابط تأكيد جديداً."); }
    catch { toast.error("تعذّر الإرسال."); }
  };

  return (
    <Shell title="الملف الشخصي" subtitle="تحكّم بمعلومات حسابك وتفضيلاتك." testId="profile-page">
      <Card title="معلومات الحساب" testId="profile-account">
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-ink-700">البريد الإلكتروني</label>
            <div className="mt-1.5 flex items-center gap-2">
              <Input value={user.email} disabled dir="ltr" data-testid="profile-email" />
              {user.email_verified ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-[11px] font-medium text-emerald-700"><CheckCircle2 className="h-3 w-3" /> مؤكَّد</span>
              ) : (
                <button type="button" data-testid="profile-resend-verify" onClick={onResendVerify}
                        className="whitespace-nowrap text-xs font-medium text-intel-800 underline">أرسل رابط التأكيد مجدّداً</button>
              )}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-700">الاسم الكامل</label>
            <Input data-testid="profile-name" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} />
          </div>
        </div>
      </Card>

      <Card title="التفضيلات" testId="profile-preferences">
        <div>
          <label className="block text-xs font-medium text-ink-700">الدولة المفضّلة</label>
          <select data-testid="profile-country" value={form.country} onChange={(e) => setForm(f => ({ ...f, country: e.target.value }))}
                  className="mt-1.5 block w-full rounded-md border border-surface-200 bg-white px-3 py-2.5 text-sm focus:border-intel-500 focus:outline-none focus:ring-2 focus:ring-intel-100">
            <option value="">أي دولة</option>
            {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="mt-4">
          <label className="block text-xs font-medium text-ink-700">القطاعات المفضّلة</label>
          <div data-testid="profile-industries" className="mt-2 flex flex-wrap gap-2">
            {INDUSTRIES.map(ind => {
              const on = form.industries.includes(ind);
              return (
                <button key={ind} type="button" onClick={() => toggle(ind)} data-testid={`profile-industry-${ind}`}
                        className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${on ? "border-intel-800 bg-intel-50 text-intel-800" : "border-surface-200 text-ink-700 hover:border-intel-300"}`}>
                  {ind}
                </button>
              );
            })}
          </div>
        </div>
      </Card>

      <div className="flex flex-col-reverse items-stretch justify-between gap-3 sm:flex-row sm:items-center">
        <button type="button" data-testid="profile-logout" onClick={onLogout}
                className="rounded-md border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50">تسجيل الخروج</button>
        <button type="button" onClick={onSave} disabled={busy} data-testid="profile-save"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-intel-800 px-5 py-2.5 text-sm font-medium text-white hover:bg-intel-700 disabled:opacity-70">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          حفظ التغييرات
        </button>
      </div>
    </Shell>
  );
}

/* ─── Change Password ─── */
export function ChangePasswordPage() {
  const { user, isLoading } = useAuth();
  const [f, setF] = useState({ cur: "", nxt: "" });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  if (isLoading) return <Shell title="تغيير كلمة المرور" testId="change-pw-loading">جاري التحميل…</Shell>;
  if (!user) return <Navigate to="/account/sign-in" replace />;

  const onSubmit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      await authApi.changePassword(f.cur, f.nxt);
      toast.success("تمّ تحديث كلمة المرور بنجاح");
      setF({ cur: "", nxt: "" });
    } catch (e2) {
      setErr(e2?.status === 400 ? "كلمة المرور الحالية غير صحيحة." : "تعذّر التنفيذ الآن.");
    } finally { setBusy(false); }
  };

  return (
    <Shell title="تغيير كلمة المرور" testId="change-pw-page">
      <Card testId="change-pw-card">
        <form onSubmit={onSubmit} data-testid="change-pw-form" className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-ink-700">كلمة المرور الحالية</label>
            <Input type="password" required data-testid="change-pw-current" value={f.cur} onChange={(e) => setF(v => ({ ...v, cur: e.target.value }))} />
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-700">كلمة المرور الجديدة (8+ أحرف)</label>
            <Input type="password" required minLength={8} data-testid="change-pw-new" value={f.nxt} onChange={(e) => setF(v => ({ ...v, nxt: e.target.value }))} />
          </div>
          {err && <div data-testid="change-pw-error" className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">{err}</div>}
          <button type="submit" disabled={busy} data-testid="change-pw-submit"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-intel-800 px-5 py-2.5 text-sm font-medium text-white hover:bg-intel-700 disabled:opacity-70">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            تحديث كلمة المرور
          </button>
        </form>
      </Card>
    </Shell>
  );
}

/* ─── Onboarding (post-registration first-run) ─── */
export function OnboardingPage() {
  const { user, isLoading, refreshUser } = useAuth();
  const nav = useNavigate();
  const [country, setCountry] = useState("");
  const [industries, setIndustries] = useState([]);
  const [busy, setBusy] = useState(false);

  if (isLoading) return <Shell title="مرحباً" testId="onboarding-loading">جاري التحميل…</Shell>;
  if (!user) return <Navigate to="/account/sign-in" replace />;
  // Skip onboarding if the user already saved preferences.
  if ((user.preferred_country || (user.preferred_industries || []).length > 0)) return <Navigate to="/" replace />;

  const toggle = (v) => setIndustries(arr => arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v]);

  const finish = async (skip = false) => {
    setBusy(true);
    try {
      await authApi.updateProfile({
        fullName: user.full_name,
        preferredCountry: skip ? null : (country || null),
        preferredIndustries: skip ? [] : industries,
      });
      await refreshUser?.();
      nav("/", { replace: true });
    } catch { toast.error("تعذّر الحفظ. سنُتيح لك تعديل التفضيلات لاحقاً من الملف الشخصي."); nav("/", { replace: true }); }
    finally { setBusy(false); }
  };

  return (
    <Shell title={`أهلاً ${user.full_name || ""} 👋`} subtitle="اختر الدولة والقطاعات التي تهمّك لتخصيص الفرص المعروضة عليك." testId="onboarding-page">
      <Card testId="onboarding-card">
        <div>
          <label className="block text-xs font-medium text-ink-700">الدولة</label>
          <select data-testid="onboarding-country" value={country} onChange={(e) => setCountry(e.target.value)}
                  className="mt-1.5 block w-full rounded-md border border-surface-200 bg-white px-3 py-2.5 text-sm">
            <option value="">أي دولة</option>
            {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="mt-6">
          <label className="block text-xs font-medium text-ink-700">القطاعات (يمكن اختيار أكثر من قطاع)</label>
          <div data-testid="onboarding-industries" className="mt-2 flex flex-wrap gap-2">
            {INDUSTRIES.map(i => {
              const on = industries.includes(i);
              return <button key={i} type="button" onClick={() => toggle(i)} data-testid={`onboarding-industry-${i}`}
                             className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${on ? "border-intel-800 bg-intel-50 text-intel-800" : "border-surface-200 text-ink-700 hover:border-intel-300"}`}>
                       {i}
                     </button>;
            })}
          </div>
        </div>
        <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button type="button" onClick={() => finish(true)} disabled={busy} data-testid="onboarding-skip" className="rounded-md border border-surface-200 bg-white px-4 py-2 text-sm text-ink-700 hover:border-intel-300">تخطّي الآن</button>
          <button type="button" onClick={() => finish(false)} disabled={busy} data-testid="onboarding-finish"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-intel-800 px-5 py-2.5 text-sm font-medium text-white hover:bg-intel-700 disabled:opacity-70">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            حفظ ومتابعة
          </button>
        </div>
      </Card>
    </Shell>
  );
}
